import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Folder, Check, Plus } from 'lucide-react';
import { clsx } from 'clsx';
import api from '@/api';
import { useExtensionUtil } from '@/hooks/useExtensionUtil';
import { loadSessionTabsViaExtensionOrApi } from '@/utils/extensionSessionTabs';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import { NotifyTeamFields, useNotifyTeamState } from '@/components/collaboration/NotifyTeamFields';

interface BookmarkGroup {
  id: string;
  name: string;
  icon?: string;
  sid?: string;
  sessionName?: string;
}

interface BookmarkModalProps {
  isOpen: boolean;
  sessionName: string;
  sessionId: string;
  isWorkSpace: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const getEmoji = (icon?: string) => {
  if (!icon) return null;
  if (icon.length <= 2 || !/^[0-9A-Fa-f]+$/.test(icon)) {
    return icon;
  }
  try {
    const codePoint = parseInt(icon, 16);
    return String.fromCodePoint(codePoint);
  } catch {
    return icon;
  }
};

export const BookmarkModal: React.FC<BookmarkModalProps> = ({
  isOpen,
  sessionName,
  sessionId,
  isWorkSpace,
  onClose,
  onSuccess
}) => {
  const { sendCBMessage } = useExtensionUtil();
  const { t } = useTranslation('common');
  const [searchQuery, setSearchQuery] = useState('');
  const [groups, setGroups] = useState<BookmarkGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const dialogRootRef = useRef<HTMLDivElement | null>(null);
  const {
    notifyTeam,
    setNotifyTeam,
    notifyMessage,
    setNotifyMessage,
    reset: resetNotify,
  } = useNotifyTeamState(false);

  useDialogA11y(dialogRootRef, { isOpen, onClose });

  const fetchBookmarkGroups = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<{
        status: string;
        bookmarkgroup: BookmarkGroup[];
      }>(`/sessions/bookmark/group?pid=${sessionId}&type=${isWorkSpace ? 'workspace' : 'unsaved'}`);

      if (response.data?.status === 'success' && Array.isArray(response.data.bookmarkgroup)) {
        setGroups(response.data.bookmarkgroup);
      }
    } catch (err) {
      console.error('Failed to fetch bookmark groups:', err);
      setError('Failed to load bookmark groups');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isWorkSpace]);

  useEffect(() => {
    if (isOpen) {
      fetchBookmarkGroups();
    } else {
      setSearchQuery('');
      setSelectedGroupId(null);
      setNewGroupName('');
      setError(null);
      setShowCreateGroup(false);
      resetNotify();
    }
  }, [isOpen, fetchBookmarkGroups]);

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;

    setIsCreating(true);
    setError(null);
    try {
      const response = await api.post<{
        status: string;
        message: string;
        bookmarkgroup: BookmarkGroup[];
      }>('/sessions/bookmark/group', {
        name: newGroupName.trim(),
        pid: sessionId
      });

      if (response.data?.status === 'success') {
        // Add new group to list and select it
        const newGroup = response.data.bookmarkgroup[0];
        if (newGroup) {
          setGroups(prev => [...prev, newGroup]);
          setSelectedGroupId(newGroup.id);
          setNewGroupName('');
        }
      }
    } catch (err) {
      console.error('Failed to create bookmark group:', err);
      setError('Failed to create group');
    } finally {
      setIsCreating(false);
    }
  };

  const handleBookmark = async () => {
    if (!selectedGroupId) return;

    setIsLoading(true);
    try {
      const { tabData } = await loadSessionTabsViaExtensionOrApi(sendCBMessage, sessionId);
      const troveData: Array<{
        title: string;
        url: string;
        favIconUrl: string;
        pid: string;
        pbgroup: string;
      }> = [];

      Object.values(tabData).forEach((tab) => {
        if (tab && typeof tab.url === 'string' && tab.url.length > 0) {
          troveData.push({
            title: tab.title || 'Untitled',
            url: tab.url,
            favIconUrl: tab.favIconUrl || '',
            pid: sessionId,
            pbgroup: selectedGroupId,
          });
        }
      });

      if (troveData.length > 0) {
        await api.post('/troveBulk', {
          troveArr: JSON.stringify(troveData),
          ...(notifyTeam
            ? { notifyTeam: true, notifyMessage: notifyMessage.trim() || undefined }
            : {}),
        });
        console.log('Added', troveData.length, 'tabs to bookmark group');
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to bookmark session:', err);
      setError('Failed to bookmark session');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        ref={dialogRootRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bookmark-modal-title"
        tabIndex={-1}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg z-50 focus:outline-none"
      >
        <div className="bg-card border border-border modal-surface shadow-modal overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border-muted">
            <div className="flex-1">
              <h2 id="bookmark-modal-title" className="text-base font-semibold text-foreground">
                {t('shared.modals.bookmark.title')}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('shared.modals.bookmark.subtitle', { name: sessionName })}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="text-muted-foreground hover:text-foreground transition-colors p-1.5 hover:bg-muted rounded-lg"
            >
              <X size={18} aria-hidden />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-4 py-2 bg-rose-500/10 border-b border-rose-500/20 text-sm text-rose-600">
              {error}
            </div>
          )}

          {/* Search */}
          <div className="px-4 py-3 border-b border-border-muted space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('shared.modals.bookmark.searchGroups')}
                  className="w-full pl-9 pr-3 py-2 bg-muted/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                  autoFocus
                />
              </div>
              <button
                onClick={() => setShowCreateGroup(!showCreateGroup)}
                className="px-3 py-2 text-sm font-medium flex items-center gap-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-all"
                title={t('shared.modals.bookmark.createNewGroup')}
              >
                <Plus size={16} />
              </button>
            </div>

            {/* Create New Group Card - Shown on button click */}
            {showCreateGroup && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 space-y-2">
              <label className="text-xs font-semibold text-foreground flex items-center gap-2">
                <Plus size={14} className="text-primary" />
                {t('shared.modals.bookmark.createNewGroup')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateGroup();
                  }}
                  placeholder={t('shared.modals.bookmark.groupNamePlaceholder')}
                  className="flex-1 px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                />
                <button
                  onClick={handleCreateGroup}
                  disabled={!newGroupName.trim() || isCreating}
                  className={clsx(
                    "px-3.5 py-2 rounded-lg transition-all font-medium text-sm flex items-center gap-1.5 flex-shrink-0",
                    newGroupName.trim() && !isCreating
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-muted text-muted-foreground/40 cursor-not-allowed"
                  )}
                >
                  {isCreating ? (
                    <>
                      <div className="w-3 h-3 border-1.5 border-current border-t-transparent rounded-full animate-spin" />
                      {t('shared.modals.bookmark.creating')}
                    </>
                  ) : (
                    <>
                      <Plus size={14} />
                      {t('shared.modals.bookmark.create')}
                    </>
                  )}
                </button>
              </div>
            </div>
            )}
          </div>

          {/* Groups List */}
          <div className="max-h-[360px] overflow-y-auto custom-scrollbar">
            {isLoading && filteredGroups.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <Folder size={40} className="text-muted-foreground/30 mb-2" />
                <p className="text-sm font-medium text-muted-foreground">
                  {searchQuery ? t('shared.modals.bookmark.noGroupsFound') : t('shared.modals.bookmark.noGroupsYet')}
                </p>
                <p className="text-xs text-muted-foreground/60 mt-0.5">
                  {!searchQuery && t('shared.modals.bookmark.createGroupHint')}
                </p>
              </div>
            ) : (
              <div className="py-1 px-2 space-y-0.5">
                {filteredGroups.map((group) => {
                  const emoji = getEmoji(group.icon);
                  const isSelected = selectedGroupId === group.id;

                  return (
                    <button
                      key={group.id}
                      onClick={() => setSelectedGroupId(group.id)}
                      className={clsx(
                        "w-full flex items-center gap-2.5 py-2 px-3 rounded-lg transition-all group",
                        isSelected
                          ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                      )}
                    >
                      <div className={clsx(
                        "p-1.5 rounded-md transition-colors flex items-center justify-center w-7 h-7 flex-shrink-0",
                        isSelected ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground group-hover:text-foreground/80"
                      )}>
                        {emoji ? (
                          <span className="text-sm leading-none">{emoji}</span>
                        ) : (
                          <Folder size={14} className={clsx(isSelected && "fill-primary/10")} />
                        )}
                      </div>
                      <span className={clsx(
                        "text-sm truncate flex-1 text-left",
                        isSelected ? "font-semibold text-foreground" : "font-medium"
                      )}>
                        {group.name}
                      </span>
                      {isSelected && (
                        <Check size={16} className="text-primary flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border-muted px-4 py-3 space-y-3">
            <NotifyTeamFields
              projectId={sessionId}
              notifyTeam={notifyTeam}
              onNotifyTeamChange={setNotifyTeam}
              notifyMessage={notifyMessage}
              onNotifyMessageChange={setNotifyMessage}
              disabled={isLoading}
            />
            <div className="flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-all"
            >
              {t('shared.modals.bookmark.cancel')}
            </button>
            <button
              onClick={handleBookmark}
              disabled={!selectedGroupId || isLoading}
              className={clsx(
                "px-4 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-1.5",
                selectedGroupId && !isLoading
                  ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                  : "bg-muted text-muted-foreground/40 cursor-not-allowed"
              )}
            >
              {t('shared.modals.bookmark.bookmark')}
            </button>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: hsl(var(--muted-foreground) / 0.15);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: hsl(var(--muted-foreground) / 0.3);
        }
      `}} />
    </>
  );
};
